import { head, prop, propEq, update } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { listDomains } from 'account/components/userManagement/domains/actions'
import { domainsByIdSelector } from 'account/components/userManagement/domains/selectors'
import {
  listAllDomainTenants,
  listUserTenants,
} from 'account/components/userManagement/tenants/new-actions'
import {
  allDomainTenantsSelector,
  userTenantsSelector,
} from 'account/components/userManagement/tenants/selectors'
import ApiClient from 'api-client/ApiClient'
import { generateApiErrorPayload, getDomainName } from 'api-client/helpers'
import { isSystemUser as isSystemUserFn } from 'app/core/utils/helpers'
import {
  getUserPermissions,
  setupKappiSession,
} from 'app/plugins/account/components/userManagement/users/new-actions'
import { cacheActions } from 'core/caching/cacheReducers'
import useListAction from 'core/hooks/useListAction'
import { permissionsActions } from 'core/session/permissionsReducers'
import type { SessionState } from 'core/session/sessionReducers'
import { sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import GlobalDropdownWithSearch, {
  type Item,
  type ItemGroup,
} from '../dropdown/GlobalDropdownWithSearch'

const GlobalTenantDropdown = () => {
  const { keystone } = ApiClient.getInstance()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentTenant, currentTenantName } = prefs

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { isSsoToken, isSystemUser, unscopedToken } = session
  const dispatch = useDispatch()

  const { loading: loadingTenants } = useListAction(listUserTenants)
  const tenants = useSelector(userTenantsSelector)

  const { loading: loadingDomainTenants } = useListAction(listAllDomainTenants)
  const domainTenants = useSelector(allDomainTenantsSelector)

  const { loading: loadingDomains } = useListAction(listDomains)
  const domainsById = useSelector(domainsByIdSelector)

  const dropdownTenants = useMemo(() => {
    return isSystemUser ? domainTenants : tenants
  }, [tenants, domainTenants, isSystemUser])

  // Dropdown options
  const dropdownOptions: Item[] = useMemo(() => {
    return dropdownTenants.map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    }))
  }, [dropdownTenants])

  const groupedDropdownOptions: ItemGroup[] = useMemo(() => {
    return dropdownTenants
      .reduce((accum, tenant) => {
        const accumIndex = accum.findIndex(
          (group) =>
            group.label === domainsById[tenant.domain_id]?.name || group.label === tenant.domain_id,
        )
        if (accumIndex >= 0) {
          return update(
            accumIndex,
            {
              ...accum[accumIndex],
              items: [
                ...accum[accumIndex].items,
                {
                  label: tenant.name,
                  value: tenant.id,
                },
              ],
            },
            accum,
          )
        } else {
          return [
            ...accum,
            {
              label: domainsById[tenant.domain_id]?.name || tenant.domain_id,
              items: [
                {
                  label: tenant.name,
                  value: tenant.id,
                },
              ],
            },
          ]
        }
      }, [])
      .map((group) => ({
        ...group,
        label: `Domain: ${group.label}`,
      }))
  }, [dropdownTenants, domainsById])

  // Set currentTenant in prefs on first-ever load
  useEffect(() => {
    // If tenants are not loaded, do nothing
    if (!dropdownTenants?.length) {
      return
    }

    // If currentTenant is not set, set it or it will default to the first tenant
    const currentIsValid = dropdownTenants?.some((t) => t?.id === currentTenant)
    if (!currentTenant || !currentTenantName || !currentIsValid) {
      const defaultTenant =
        dropdownTenants?.find(propEq('name', 'service')) || head(dropdownTenants) // fallback to first one
      updatePrefs({
        currentTenant: defaultTenant?.id,
        currentTenantName: defaultTenant?.name,
      })
    }
  }, [dropdownTenants])

  const updateCurrentTenant = async (tenant: Item) => {
    // const tenant = tenants?.find(propEq('name', tenantName))
    if (!tenant?.value || !tenant?.label) {
      return
    }

    const { user, role, scopedToken } = await keystone.changeProjectScopeWithToken(
      tenant.value,
      isSsoToken,
    )

    // Find the tenant in tenants list
    const matchedTenant = tenants?.find(propEq('id', tenant.value))

    // Refetch dex token
    await setupKappiSession({
      username: '',
      password: unscopedToken,
      domain: getDomainName(),
      activeTenant: matchedTenant,
    })

    dispatch(cacheActions.clearCache())
    dispatch(sessionActions.resetSession({})) // Reset session params when tenant changes

    if (isSystemUserFn()) {
      // Same system token is used across all tenants/domains, do not refresh
      // the token
      dispatch(
        sessionActions.updateSession({
          userDetails: { ...user, role },
          activeTenant: tenant.label,
          vmsSearchParams: null,
        }),
      )
      return
    } else {
      dispatch(
        sessionActions.updateSession({
          userDetails: { ...user, role },
          scopedToken,
          activeTenant: tenant.label,
          vmsSearchParams: null,
        }),
      )
    }

    // Permissions are per-tenant/domain; clear the stale map before refetching so
    // permission-gated UI fails closed instead of showing the previous tenant's grants.
    dispatch(permissionsActions.clearPermissions())
    try {
      const permissions = await getUserPermissions()
      dispatch(permissionsActions.updatePermissions(permissions))
    } catch (err) {
      console.error(
        'Failed to refresh permissions after tenant switch',
        generateApiErrorPayload(err).message,
      )
      dispatch(permissionsActions.clearPermissions())
    }

    // Create Appication Credential for tenant-user
    // if (isKaapiEnabled() && isKaapiTokenAvailable()) {
    //   createKaapiApplicationCredentials(ApplicationCredentialType.IdentityRef)
    // }
  }

  const handleTenantSelect = useCallback(
    async (newTenant: Item) => {
      if (!newTenant?.value || !newTenant?.label) {
        return
      }

      // Set loadingTenant to true
      dispatch(
        sessionActions.updateSession({
          loadingTenant: true,
        }),
      )

      await updateCurrentTenant(newTenant)
      updatePrefs({
        currentTenant: newTenant?.value,
        currentTenantName: newTenant?.label,
        allTenants: false, //Reset AllTenants to false when tenant is changed
      })

      // Set loadingTenant to false
      dispatch(
        sessionActions.updateSession({
          loadingTenant: false,
        }),
      )
    },
    [tenants],
  )

  return (
    <GlobalDropdownWithSearch
      label="Tenant"
      items={dropdownOptions}
      groupedItems={isSystemUser ? groupedDropdownOptions : null}
      value={String(currentTenant)}
      onChange={handleTenantSelect}
      loading={loadingTenants || loadingDomainTenants || loadingDomains}
    />
  )
}

export default GlobalTenantDropdown
